/**
 * Ephemeral storage and redaction helpers for the Gmail add-on link attempt.
 *
 * The attempt is a bearer secret. Keep it in this module's memory only: never
 * put it in router state, Redux, Web Storage, diagnostics, or a subsequent URL.
 */
export const GMAIL_CONNECT_PATH = '/integrations/gmail/connect';

const ATTEMPT_PARAMETER = 'attempt';
const MAX_ATTEMPT_LENGTH = 4096;
const REDACTED = '[REDACTED]';
const GMAIL_CONNECT_DOCUMENT_PATHS = [
  GMAIL_CONNECT_PATH,
  GMAIL_CONNECT_PATH + '/',
  GMAIL_CONNECT_PATH + '/index.html',
];
const FIRST_PARTY_HOSTS = [
  'co-parenting.app',
  'www.co-parenting.app',
  // Recognize previously issued links without changing their destination.
  'varannanvecka.app',
  'www.varannanvecka.app',
];

let pendingAttempt: string | undefined;
const knownSensitiveValues: string[] = [];

function isValidAttempt(value: string | null): value is string {
  return !!value &&
    value.length <= MAX_ATTEMPT_LENGTH &&
    !/[\u0000-\u001f\u007f]/.test(value);
}

function attemptFromUrl(parsed: URL): string | null {
  const query = parsed.searchParams.getAll(ATTEMPT_PARAMETER);
  const fragment = new URLSearchParams(parsed.hash.slice(1)).getAll(ATTEMPT_PARAMETER);
  // New add-on links use fragments. Keep legacy query links during rollout,
  // but reject duplicate/ambiguous credentials instead of choosing one.
  const values = query.concat(fragment);
  return values.length === 1 ? values[0] : null;
}

function rememberSensitiveValue(value: string): void {
  if (!value || knownSensitiveValues.indexOf(value) !== -1) {
    return;
  }

  // A page can only have one useful link attempt, but retain a few replaced
  // values so asynchronous error reports cannot reveal an already-cleared one.
  knownSensitiveValues.push(value);
  if (knownSensitiveValues.length > 8) {
    knownSensitiveValues.shift();
  }
}

export function isGmailConnectPath(path: string): boolean {
  if (!path) {
    return false;
  }

  try {
    const parsed = new URL(path, 'https://co-parenting.app');
    return GMAIL_CONNECT_DOCUMENT_PATHS.indexOf(parsed.pathname) !== -1;
  } catch {
    return GMAIL_CONNECT_DOCUMENT_PATHS.indexOf(path.split('?')[0].split('#')[0]) !== -1;
  }
}

function isFirstPartyConnectUrl(parsed: URL, originalValue: string): boolean {
  if (!isGmailConnectPath(parsed.pathname)) {
    return false;
  }

  const isSafeRelative = originalValue.charAt(0) === '/' &&
    originalValue.slice(0, 2) !== '//' &&
    originalValue.indexOf('\\') === -1 &&
    !/[\u0000-\u001f\u007f]/.test(originalValue);
  return isSafeRelative || (
    parsed.protocol === 'https:' &&
    FIRST_PARTY_HOSTS.indexOf(parsed.hostname.toLowerCase()) !== -1
  );
}

function captureFromParsedUrl(parsed: URL, originalValue: string): string | undefined {
  if (!isFirstPartyConnectUrl(parsed, originalValue)) {
    return pendingAttempt;
  }

  const value = attemptFromUrl(parsed);
  if (isValidAttempt(value)) {
    pendingAttempt = value;
    rememberSensitiveValue(value);
  }

  return pendingAttempt;
}

/** Capture a first-party Gmail connect URL without navigating to the secret URL. */
export function captureGmailAttemptFromUrl(url: string): string | undefined {
  try {
    const parsed = new URL(url, 'https://co-parenting.app');
    return captureFromParsedUrl(parsed, url);
  } catch {
    return pendingAttempt;
  }
}

/**
 * Capture and synchronously remove the attempt from the browser address bar.
 * A clean login return must not erase an attempt already held in memory.
 */
export function captureGmailAttemptFromCurrentUrl(): string | undefined {
  if (typeof window === 'undefined' || !window.location || !window.history) {
    return pendingAttempt;
  }

  try {
    const currentUrl = new URL(window.location.href);
    if (!isGmailConnectPath(currentUrl.pathname)) {
      return pendingAttempt;
    }

    // The current origin may be localhost/a preview environment; the browser
    // has already enforced same-origin navigation, so only the exact path is
    // relevant here.
    const value = attemptFromUrl(currentUrl);
    if (isValidAttempt(value)) {
      pendingAttempt = value;
      rememberSensitiveValue(value);
    } else if (currentUrl.search || currentUrl.hash) {
      pendingAttempt = undefined;
    }
    if (currentUrl.pathname !== GMAIL_CONNECT_PATH || currentUrl.search || currentUrl.hash) {
      // This route has no legitimate URL state. Drop all query/hash data so a
      // URL-supplied target ID (or another accidental secret) cannot linger.
      // Canonicalize the slash and index-document aliases before React Router
      // sees them so every browser/CDN form reaches the same exact SPA route.
      window.history.replaceState(window.history.state, '', GMAIL_CONNECT_PATH);
    }
  } catch {
    // Deliberately do not include the malformed URL or attempt in diagnostics.
  }

  return pendingAttempt;
}

export function getPendingGmailAttempt(): string | undefined {
  return pendingAttempt;
}

export function clearPendingGmailAttempt(): void {
  pendingAttempt = undefined;
}

function replaceKnownValues(value: string): string {
  let result = value;
  knownSensitiveValues.forEach(secret => {
    result = result.split(secret).join(REDACTED);
    try {
      result = result.split(encodeURIComponent(secret)).join(REDACTED);
    } catch {
      // The literal replacement above still protects malformed input.
    }
  });
  return result;
}

/** Removes the attempt query value from a URL before telemetry captures it. */
export function sanitizeUrlForTelemetry(value: string): string {
  if (!value) {
    return value;
  }

  let sanitized = replaceKnownValues(String(value));
  sanitized = sanitized
    .replace(/([?&#])attempt=[^&#\s]*&?/gi, (matchedText, separator) =>
      (separator === '?' || separator === '#') && /&$/.test(matchedText) ? separator : '')
    .replace(/(?:^|%26|%23)attempt%3[dD][^%&#\s]*/gi, '')
    .replace(/\?(?=#|\s|$)/g, '')
    .replace(/&&+/g, '&');
  return sanitized;
}

/** Redacts sensitive URL, JSON, and form representations in diagnostic text. */
export function sanitizeTelemetryText(value: unknown): string {
  let text: string;
  try {
    text = typeof value === 'string' ? value : String(value);
  } catch {
    return '<unavailable>';
  }

  return sanitizeUrlForTelemetry(text)
    .replace(/("attempt"\s*:\s*")[^"]*(")/gi, '$1' + REDACTED + '$2')
    .replace(/(\battempt\s*[=:]\s*)[^\s,;&}\]]+/gi, '$1' + REDACTED)
    .replace(/("(?:googleSub|forwardingToken|forwarding_token|emailAddress|emailToken)"\s*:\s*")[^"]*(")/gi, '$1' + REDACTED + '$2')
    .replace(/[A-Za-z0-9_-]{1,64}@data\.co-parenting\.app/gi, REDACTED);
}

function isSensitiveKey(key: string): boolean {
  const normalized = key.replace(/[-_]/g, '').toLowerCase();
  return normalized === 'attempt' ||
    normalized === 'googlesub' ||
    normalized === 'forwardingtoken' ||
    normalized === 'emailaddress' ||
    normalized === 'emailtoken';
}

/** Makes a cycle-safe, getter-safe copy suitable for console/error reporting. */
export function sanitizeTelemetryValue(value: any, seen: any[] = []): any {
  if (value === null || value === undefined || typeof value === 'boolean' || typeof value === 'number') {
    return value;
  }
  if (typeof value === 'string') {
    return sanitizeTelemetryText(value);
  }
  if (typeof value === 'function') {
    return `[function ${value.name || 'anonymous'}]`;
  }
  if (seen.indexOf(value) !== -1) {
    return '[Circular]';
  }

  seen.push(value);
  const output: any = Array.isArray(value) ? [] : {};
  const keys = value instanceof Error
    ? ['name', 'message', 'stack']
    : Object.keys(value).slice(0, 50);

  keys.forEach(key => {
    if (isSensitiveKey(key)) {
      output[key] = REDACTED;
      return;
    }
    try {
      output[key] = sanitizeTelemetryValue(value[key], seen);
    } catch {
      output[key] = '<unavailable>';
    }
  });
  seen.pop();
  return output;
}

// This module is deliberately imported before the application graph in
// index.tsx. Capture at module evaluation time so App/analytics code cannot see
// the bearer value in window.location.
captureGmailAttemptFromCurrentUrl();



// WEBPACK FOOTER //
// ./src/integrations/gmail/gmailAttemptVault.ts